import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { leadsApi, type SarvamOpdPerformanceReport } from "@/api/leads";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

type Props = {
  from: string;
  to: string;
  hospitalId?: string;
};

function formatInr(n: number): string {
  return `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

export function LeadSarvamOpdPerformanceTab({ from, to, hospitalId }: Props) {
  const params = { from, to, ...(hospitalId ? { hospitalId } : {}) };

  const { data, isLoading, isError, error } = useQuery<SarvamOpdPerformanceReport>({
    queryKey: ["sarvam-opd-performance", params],
    queryFn: async () => (await leadsApi.getSarvamOpdPerformanceReport(params)).data.data,
  });

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center gap-2 py-12 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
          Loading Sarvam OPD performance…
        </CardContent>
      </Card>
    );
  }

  if (isError) {
    const message =
      (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
      (error as Error)?.message ||
      "Failed to load report";
    return (
      <Card>
        <CardContent className="py-8 text-sm text-destructive">{message}</CardContent>
      </Card>
    );
  }

  const rows = data?.rows ?? [];
  const totals = data?.totals;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Sarvam booked OPDs — performance</CardTitle>
        <p className="text-sm text-muted-foreground font-normal">
          Leads in the Sarvam OPD funnel (Sarvam booked OPD activity), attributed to the current CRM
          lead owner. OPD visited uses OPD consultation bills in the date range (free vs paid by net
          amount). IPD counts discharged admissions with discharge date in range.
        </p>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Sales person</TableHead>
              <TableHead className="text-right">OPD booked</TableHead>
              <TableHead className="text-right">OPD visited (free)</TableHead>
              <TableHead className="text-right">OPD visited (paid)</TableHead>
              <TableHead className="text-right">OPD bill value</TableHead>
              <TableHead className="text-right">IPD done</TableHead>
              <TableHead className="text-right">IPD discharge value</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                  No data in this range.
                </TableCell>
              </TableRow>
            ) : (
              <>
                {rows.map((r) => (
                  <TableRow key={r.staffId}>
                    <TableCell className="font-medium">{r.staffName}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.opdBooked}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.opdVisitedFree}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.opdVisitedPaid}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatInr(r.opdBillValue)}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.ipdDone}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatInr(r.ipdDischargeBillValue)}
                    </TableCell>
                  </TableRow>
                ))}
                {totals ? (
                  <TableRow className="bg-muted/40 font-semibold">
                    <TableCell>{totals.staffName}</TableCell>
                    <TableCell className="text-right tabular-nums">{totals.opdBooked}</TableCell>
                    <TableCell className="text-right tabular-nums">{totals.opdVisitedFree}</TableCell>
                    <TableCell className="text-right tabular-nums">{totals.opdVisitedPaid}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatInr(totals.opdBillValue)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{totals.ipdDone}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatInr(totals.ipdDischargeBillValue)}
                    </TableCell>
                  </TableRow>
                ) : null}
              </>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
